"use client"

import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { BiTime } from "react-icons/bi"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import { cn } from "@/lib/utils"

// Initialize dayjs plugins
dayjs.extend(utc)
dayjs.extend(timezone)

interface ICountdownTimerProps {
  deadline: string
  onExpire?: () => void
  className?: string
}

interface ITimeUnit {
  value: number
  label: string
  prevValue?: number
}

export function CountdownTimer({ deadline, onExpire, className }: ICountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<{
    days: number
    hours: number
    minutes: number
    seconds: number
    prevSeconds?: number
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0 })

  useEffect(() => {
    const calculateTimeLeft = () => {
      // Convert UTC deadline to Asia/Ho_Chi_Minh timezone
      const deadlineInVN = dayjs(deadline).tz("Asia/Ho_Chi_Minh")
      const nowInVN = dayjs().tz("Asia/Ho_Chi_Minh")
      const difference = deadlineInVN.diff(nowInVN)
      
      if (difference <= 0) {
        onExpire?.()
        return { days: 0, hours: 0, minutes: 0, seconds: 0 }
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60)
      }
    }

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft())
    }, 1000)

    return () => clearInterval(timer)
  }, [deadline, onExpire])

  const StaticTimeUnit = ({ value, label }: Omit<ITimeUnit, 'prevValue'>) => (
    <div className="flex flex-col items-center">
      <div className="relative h-10 min-w-[2.5rem] overflow-hidden rounded-lg bg-gradient-to-b from-red-500 to-red-600 p-[2px]">
        <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent" />
        <div className="relative flex h-full w-full items-center justify-center rounded-[6px] bg-gradient-to-b from-red-500 to-red-600 px-2 font-mono">
          <span className="font-bold text-lg text-white">
            {value.toString().padStart(2, '0')}
          </span>
        </div>
      </div>
      <span className="mt-1 font-medium text-gray-600 text-xs">{label}</span>
    </div>
  )

  const AnimatedSeconds = ({ value, label }: Omit<ITimeUnit, 'prevValue'>) => (
    <div className="flex flex-col items-center">
      <div className="relative h-10 min-w-[2.5rem] overflow-hidden rounded-lg bg-gradient-to-b from-red-500 to-red-600 p-[2px]">
        <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent" />
        <div className="relative flex h-full w-full items-center justify-center rounded-[6px] bg-gradient-to-b from-red-500 to-red-600 px-2 font-mono">
          <AnimatePresence mode="popLayout">
            <motion.span
              key={value}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              className="absolute font-bold text-lg text-white"
            >
              {value.toString().padStart(2, '0')}
            </motion.span>
          </AnimatePresence>
        </div>
      </div>
      <span className="mt-1 font-medium text-gray-600 text-xs">{label}</span>
    </div>
  )

  const Separator = () => (
    <div className="flex h-[52px] items-center">
      <div className="flex flex-col gap-1">
        <div className="h-2 w-[2px] rounded-full bg-red-300" />
        <div className="h-2 w-[2px] rounded-full bg-red-300" />
      </div>
    </div>
  )

  if (timeLeft.days === 0 && timeLeft.hours === 0 && 
      timeLeft.minutes === 0 && timeLeft.seconds === 0) {
    return null
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "flex flex-col items-center gap-4 rounded-xl bg-gradient-to-r from-red-50 to-red-100 p-3 shadow-sm",
        className
      )}
    >
      <div className="flex gap-2 self-start text-red-500">
        <BiTime className="h-5 w-5" />
        <span className="font-medium text-sm">Kết thúc sau:</span>
      </div>
      <div className="flex gap-2">
        {timeLeft.days > 0 && (
          <>
            <StaticTimeUnit value={timeLeft.days} label="Ngày" />
            <Separator />
          </>
        )}
        <StaticTimeUnit value={timeLeft.hours} label="Giờ" />
        <Separator />
        <StaticTimeUnit value={timeLeft.minutes} label="Phút" />
        <Separator />
        <AnimatedSeconds 
          value={timeLeft.seconds}
          label="Giây" 
        />
      </div>
    </motion.div>
  )
} 